export type ConversationRole =
  | "workspace_owner"
  | "event_manager"
  | "game_designer"
  | "analyst"
  | "event_operator"
  | "registration"
  | "booth_host"
  | "sponsor_analyst"
  | "attendee"
  | "remote_tester";

export type ConversationCapability =
  | "workspace.manage"
  | "team.manage"
  | "event.create"
  | "event.edit"
  | "event.publish"
  | "event.control"
  | "mission.design"
  | "mission.verify"
  | "report.view"
  | "report.export"
  | "lead.view_consented"
  | "booth.manage"
  | "sponsor.view_aggregate"
  | "passport.participate"
  | "passport.manage_own_data"
  | "readiness.complete";

export type ConversationActionKind = "navigate" | "preview" | "mutate" | "approve";

export interface ConversationAction {
  id: string;
  label: string;
  kind: ConversationActionKind;
  capability?: ConversationCapability;
  route?: string;
  approvalCopy?: string;
}

export interface RoleConversationStart {
  role: ConversationRole;
  roleLabel: string;
  greeting: string;
  outcome: string;
  actions: readonly ConversationAction[];
  inspector: string;
}

const capabilities = (...values: ConversationCapability[]): readonly ConversationCapability[] => Object.freeze(values);
const actions = (...values: ConversationAction[]): readonly ConversationAction[] => Object.freeze(values);

const ROLE_ALIASES: Readonly<Record<string, ConversationRole>> = Object.freeze({
  owner: "workspace_owner",
  organizer: "workspace_owner",
  workspace_owner: "workspace_owner",
  manager: "event_manager",
  event_manager: "event_manager",
  designer: "game_designer",
  game_designer: "game_designer",
  analyst: "analyst",
  viewer: "analyst",
  operator: "event_operator",
  backup_operator: "event_operator",
  event_operator: "event_operator",
  registration: "registration",
  host: "booth_host",
  booth_host: "booth_host",
  sponsor: "sponsor_analyst",
  sponsor_analyst: "sponsor_analyst",
  attendee: "attendee",
  participant: "attendee",
  remote_beta: "remote_tester",
  beta_tester: "remote_tester",
  remote_tester: "remote_tester",
});

const ROLE_CAPABILITIES: Readonly<Record<ConversationRole, readonly ConversationCapability[]>> = Object.freeze({
  workspace_owner: capabilities(
    "workspace.manage", "team.manage", "event.create", "event.edit", "event.publish", "event.control",
    "mission.design", "mission.verify", "report.view", "report.export", "lead.view_consented",
    "booth.manage", "sponsor.view_aggregate",
  ),
  event_manager: capabilities(
    "event.create", "event.edit", "event.publish", "event.control", "mission.design", "mission.verify",
    "report.view", "report.export", "lead.view_consented", "booth.manage", "sponsor.view_aggregate",
  ),
  game_designer: capabilities("event.create", "event.edit", "mission.design", "report.view"),
  analyst: capabilities("report.view", "report.export", "sponsor.view_aggregate"),
  event_operator: capabilities("event.control", "mission.verify", "report.view"),
  registration: capabilities("mission.verify", "readiness.complete"),
  booth_host: capabilities("mission.verify", "lead.view_consented", "booth.manage", "readiness.complete"),
  sponsor_analyst: capabilities("report.view", "report.export", "sponsor.view_aggregate", "readiness.complete"),
  attendee: capabilities("passport.participate", "passport.manage_own_data"),
  remote_tester: capabilities("readiness.complete"),
});

const ROLE_STARTS: Readonly<Record<ConversationRole, RoleConversationStart>> = Object.freeze({
  workspace_owner: {
    role: "workspace_owner",
    roleLabel: "Workspace Owner",
    greeting: "Let’s turn your next room into measurable business momentum.",
    outcome: "A ready workspace and a clear next event decision.",
    inspector: "Workspace readiness",
    actions: actions(
      { id: "owner.use_template", label: "Use Mega Mixer template", kind: "preview", capability: "event.create" },
      { id: "owner.start_brief", label: "Start from a brief", kind: "navigate", capability: "event.create", route: "/studio/new" },
      { id: "owner.open_event", label: "Open an event", kind: "navigate", capability: "event.edit", route: "/studio" },
    ),
  },
  event_manager: {
    role: "event_manager",
    roleLabel: "Event Manager",
    greeting: "I’ll keep the room, staff, timing, and risks in one operational thread.",
    outcome: "An event plan with owners, blockers, and the next safe action.",
    inspector: "Timeline and blockers",
    actions: actions(
      { id: "manager.prepare", label: "Prepare upcoming event", kind: "navigate", capability: "event.edit", route: "/studio" },
      { id: "manager.live", label: "Open live control", kind: "navigate", capability: "event.control", route: "/studio" },
      { id: "manager.readiness", label: "Review readiness", kind: "preview", capability: "report.view" },
    ),
  },
  game_designer: {
    role: "game_designer",
    roleLabel: "Game Designer",
    greeting: "Describe the outcome; I’ll shape it into a playable event journey.",
    outcome: "A previewable mission draft with scoring and verification.",
    inspector: "Attendee preview",
    actions: actions(
      { id: "designer.generate", label: "Generate a game", kind: "navigate", capability: "mission.design", route: "/studio" },
      { id: "designer.edit", label: "Edit mission set", kind: "navigate", capability: "mission.design", route: "/studio" },
      { id: "designer.preview", label: "Preview attendee journey", kind: "preview", capability: "report.view" },
    ),
  },
  analyst: {
    role: "analyst",
    roleLabel: "Analyst",
    greeting: "I’ll separate verified outcomes from directional signals.",
    outcome: "A decision-ready performance summary with transparent definitions.",
    inspector: "Metrics and provenance",
    actions: actions(
      { id: "analyst.executive", label: "Executive view", kind: "preview", capability: "report.view" },
      { id: "analyst.sponsor", label: "Sponsor ROI", kind: "preview", capability: "sponsor.view_aggregate" },
      { id: "analyst.twin", label: "Economic Twin", kind: "preview", capability: "report.view" },
    ),
  },
  event_operator: {
    role: "event_operator",
    roleLabel: "Event Operator",
    greeting: "I’ll surface only the event-day decisions that need you now.",
    outcome: "A safe next action with current room state and consequences.",
    inspector: "Room state and incidents",
    actions: actions(
      { id: "operator.open", label: "Open doors", kind: "approve", capability: "event.control", approvalCopy: "Start the event and allow verified activity to count?" },
      { id: "operator.entry", label: "Check entry flow", kind: "preview", capability: "report.view" },
      { id: "operator.issue", label: "Resolve an issue", kind: "navigate", capability: "event.control" },
    ),
  },
  registration: {
    role: "registration",
    roleLabel: "Registration",
    greeting: "I’ll help each guest pass the correct Entry QR gate without a queue.",
    outcome: "A verified check-in and a clear attendee handoff.",
    inspector: "Entry flow",
    actions: actions(
      { id: "registration.entry", label: "Show Entry QR", kind: "preview", capability: "mission.verify" },
      { id: "registration.reuse", label: "Help with Passport", kind: "navigate", capability: "mission.verify" },
      { id: "registration.escalate", label: "Escalate a problem", kind: "mutate", capability: "readiness.complete" },
    ),
  },
  booth_host: {
    role: "booth_host",
    roleLabel: "Booth Host",
    greeting: "I’ll keep proof fast, private, and tied to a real conversation.",
    outcome: "A verified visitor interaction and a consent-safe follow-up state.",
    inspector: "Booth proof and lead state",
    actions: actions(
      { id: "booth.verify", label: "Verify visitor", kind: "mutate", capability: "mission.verify" },
      { id: "booth.leads", label: "Review qualified interest", kind: "preview", capability: "lead.view_consented" },
      { id: "booth.escalate", label: "Escalate an issue", kind: "mutate", capability: "readiness.complete" },
    ),
  },
  sponsor_analyst: {
    role: "sponsor_analyst",
    roleLabel: "Sponsor Analyst",
    greeting: "I’ll show what the sponsorship produced without exposing private attendee data.",
    outcome: "A privacy-safe outcome story with evidence and target progress.",
    inspector: "ROI and provenance",
    actions: actions(
      { id: "sponsor.targets", label: "See target progress", kind: "preview", capability: "sponsor.view_aggregate" },
      { id: "sponsor.interest", label: "Inspect qualified interest", kind: "preview", capability: "sponsor.view_aggregate" },
      { id: "sponsor.export", label: "Export summary", kind: "navigate", capability: "report.export" },
    ),
  },
  attendee: {
    role: "attendee",
    roleLabel: "Attendee",
    greeting: "Tell me what a successful mixer looks like for you. No typing required.",
    outcome: "A clear next move toward the attendee’s chosen destination.",
    inspector: "Personal route",
    actions: actions(
      { id: "attendee.customers", label: "Find customers", kind: "mutate", capability: "passport.participate" },
      { id: "attendee.partners", label: "Meet partners", kind: "mutate", capability: "passport.participate" },
      { id: "attendee.booths", label: "Explore booths", kind: "mutate", capability: "passport.participate" },
      { id: "attendee.help", label: "Help someone", kind: "mutate", capability: "passport.participate" },
    ),
  },
  remote_tester: {
    role: "remote_tester",
    roleLabel: "Remote Tester",
    greeting: "Run the real flow and capture the friction before event day.",
    outcome: "Completed readiness evidence from a real device and network.",
    inspector: "Test evidence",
    actions: actions(
      { id: "tester.entry", label: "Test event entry", kind: "navigate", capability: "readiness.complete" },
      { id: "tester.mission", label: "Test first mission", kind: "navigate", capability: "readiness.complete" },
      { id: "tester.feedback", label: "Submit friction", kind: "mutate", capability: "readiness.complete" },
    ),
  },
});

export function normalizeConversationRole(rawRole: string | null | undefined): ConversationRole | null {
  const key = String(rawRole || "").trim().toLowerCase().replaceAll("-", "_").replaceAll(" ", "_");
  return ROLE_ALIASES[key] || null;
}

export function conversationCapabilities(role: ConversationRole): readonly ConversationCapability[] {
  return ROLE_CAPABILITIES[role];
}

export function conversationCan(role: ConversationRole, capability: ConversationCapability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

export function conversationStart(role: ConversationRole): RoleConversationStart {
  return ROLE_STARTS[role];
}

export function visibleConversationActions(role: ConversationRole): readonly ConversationAction[] {
  return ROLE_STARTS[role].actions.filter((action) => !action.capability || conversationCan(role, action.capability));
}

export function actionRequiresApproval(action: ConversationAction): boolean {
  return action.kind === "approve";
}
